import { SerializedConstructor } from '@langchain/core/load/serializable';
import { MessagePlusCircleIcon } from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { emulateNativeClick } from '@/components/DataGrid.utils';
import { useEvaluatorStore } from '@/components/EvaluatorCrudPane/store/evaluatorStore';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Badge, Button, Tooltip } from '@/design-system';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useOrganizationId } from '@/hooks/useSwr';
import { appPromptsIndexPath } from '@/utils/constants';

export const ModelDisplay = ({
  model,
}: {
  model: SerializedConstructor | null;
}) => {
  if (!model) {
    return (
      <div className="flex items-center text-sm">
        <div className="text-tertiary">No model configuration available</div>
      </div>
    );
  }

  const getFeatureCount = () => {
    let count = 0;
    if (
      'temperature' in model.kwargs &&
      typeof model.kwargs.temperature === 'number'
    )
      count++;
    if ('max_tokens' in model.kwargs && model.kwargs.max_tokens != null)
      count++;
    if ('top_k' in model.kwargs && model.kwargs.top_k != null) count++;
    if ('top_p' in model.kwargs && typeof model.kwargs.top_p === 'number')
      count++;
    return count;
  };

  const featureCount = getFeatureCount();
  const modelName = model.kwargs?.model || model.id?.at(-1) || 'Unknown';

  return (
    <div className="flex shrink-0 items-center text-xxs">
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="cursor-pointer">
            <Badge className="border-none bg-tertiary text-primary hover:bg-quaternary">
              <>
                <span className="font-medium text-quaternary">
                  {model.id?.at(-1) || 'Unknown'}
                </span>
                <span className="text-xxs font-semibold text-secondary">
                  {modelName}
                </span>
                {featureCount > 0 && (
                  <span className="rounded-full bg-tertiary px-1.5 text-xxs text-tertiary">
                    {featureCount}
                  </span>
                )}
              </>
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3">
          <div className="mb-2 text-xs font-medium text-secondary">
            Model Params
          </div>
          <div className="space-y-1">
            {'temperature' in model.kwargs &&
              typeof model.kwargs.temperature === 'number' && (
                <div className="flex justify-between text-xxs">
                  <span className="text-tertiary">temp</span>
                  <span className="font-medium text-secondary">
                    {model.kwargs.temperature.toFixed(2)}
                  </span>
                </div>
              )}
            {'top_p' in model.kwargs &&
              typeof model.kwargs.top_p === 'number' && (
                <div className="flex justify-between text-xxs">
                  <span className="text-tertiary">top p</span>
                  <span className="font-medium text-secondary">
                    {model.kwargs.top_p.toFixed(2)}
                  </span>
                </div>
              )}
            {'max_tokens' in model.kwargs &&
              model.kwargs.max_tokens != null && (
                <div className="flex justify-between text-xxs">
                  <span className="text-tertiary">max output tokens</span>
                  <span className="font-medium text-secondary">
                    {model.kwargs.max_tokens}
                  </span>
                </div>
              )}
            {'top_k' in model.kwargs && model.kwargs.top_k != null && (
              <div className="flex justify-between text-xxs">
                <span className="text-tertiary">top k</span>
                <span className="font-medium text-secondary">
                  {model.kwargs.top_k}
                </span>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const HubPromptDisplay = () => {
  const hubRef = useEvaluatorStore((state) => state.hubRef);
  const promptIdentifier = hubRef.split(':')[0];
  const commitHash = hubRef.split(':')[1]?.slice(0, 8);
  const navigate = useNavigate();
  const organizationId = useOrganizationId();

  if (!hubRef) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      size="sm"
      color="secondary"
      className="flex shrink items-center gap-1"
      onClick={(e) => {
        const targetUrl =
          `/${appPromptsIndexPath}/${promptIdentifier}` +
          (commitHash !== 'latest' ? `/${commitHash}` : '') +
          `?organizationId=${organizationId}` +
          (commitHash !== 'latest' ? `&tab=1` : '&tab=0');
        if (!emulateNativeClick(targetUrl, e.nativeEvent)) {
          navigate(targetUrl);
        }
      }}
    >
      <MessagePlusCircleIcon className="h-4 w-4" />
      <TextOverflowTooltip
        className="flex-1"
        textProps={{ as: 'span', variant: 'xs', weight: 'normal' }}
      >
        {promptIdentifier}
      </TextOverflowTooltip>
      <Badge color="secondary" size="xs" rounded="xs">
        {commitHash}
      </Badge>
    </Button>
  );
};

export const SamplingRateDisplay = ({
  samplingRate,
}: {
  samplingRate?: number;
}) => {
  const { isDarkMode } = useColorScheme();

  const formattedRate = useMemo(() => {
    if (samplingRate == null) return '';
    const str = samplingRate.toString();
    if (!str.includes('.')) {
      return str;
    }
    const decimalPlaces = Math.min(str.split('.')[1].length, 4);
    return samplingRate.toFixed(decimalPlaces);
  }, [samplingRate]);

  if (samplingRate == null) {
    return null;
  }

  return (
    <Tooltip description="Sampling Rate">
      <span>
        <Badge
          className="border-none bg-tertiary text-primary"
          title="Sampling Rate"
        >
          <>
            <CircularProgress
              size="sm"
              determinate
              value={samplingRate * 100}
              sx={{
                '--CircularProgress-size': '12px',
                '--CircularProgress-progressThickness': '2px',
                '--CircularProgress-progressColor': isDarkMode
                  ? '#D1D1D6'
                  : '#51525C',
                '--CircularProgress-trackThickness': '2px',
                '--CircularProgress-trackColor': isDarkMode
                  ? '#1A1A1A'
                  : '#E5E5E5',
                transform: 'scaleX(-1)',
              }}
              color="neutral"
            />
            <span className="text-xxs text-tertiary">{formattedRate}</span>
          </>
        </Badge>
      </span>
    </Tooltip>
  );
};
